import type {
  Contatto,
  ContattoInput,
  EmailInviata,
  EventoRegistro,
  Impostazioni,
  RichiestaCancellazione,
  UtenteDettaglio,
} from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiaveMe } from './auth';
import { richiesta, API_BASE } from './client';
import type { Paginato } from './admin';

function qs(valori: object): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(valori as Record<string, unknown>)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const chiaviAmm = {
  contatti: ['admin', 'contatti'] as const,
  contattiAvvisi: ['admin', 'contatti', 'avvisi'] as const,
  email: (f: object) => ['admin', 'email', f] as const,
  utente: (id: string) => ['admin', 'utente', id] as const,
  impostazioni: ['admin', 'impostazioni'] as const,
  registro: (f: object) => ['admin', 'registro', f] as const,
  cancellazioni: (stato?: string) => ['admin', 'cancellazioni', stato ?? ''] as const,
  miaCancellazione: ['me', 'cancellazione'] as const,
};

// ---- contatti operativi
export function useContatti() {
  return useQuery({ queryKey: chiaviAmm.contatti, queryFn: async () => (await richiesta<Contatto[]>('/admin/contatti')).dati });
}
export function useContattiAvvisi() {
  return useQuery({ queryKey: chiaviAmm.contattiAvvisi, queryFn: async () => (await richiesta<{ report: boolean; chiamate: boolean }>('/admin/contatti/avvisi')).dati });
}
export function useSalvaContatto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id?: string; dati: ContattoInput }) =>
      (await richiesta<Contatto>(input.id ? `/admin/contatti/${input.id}` : '/admin/contatti', { method: input.id ? 'PUT' : 'POST', json: input.dati })).dati,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'contatti'] }),
  });
}
export function useDisattivaContatto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await richiesta<unknown>(`/admin/contatti/${id}/disattiva`, { method: 'POST' })).status,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'contatti'] }),
  });
}

// ---- email inviate
export interface FiltriEmail {
  q?: string;
  stato?: string;
  template?: string;
  pagina?: number;
  perPagina?: number;
}
export function useEmailInviate(filtri: FiltriEmail) {
  return useQuery({
    queryKey: chiaviAmm.email(filtri),
    queryFn: async () => (await richiesta<Paginato<EmailInviata>>(`/admin/email${qs(filtri)}`)).dati,
    placeholderData: (prev) => prev,
  });
}
export function useReinviaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await richiesta<unknown>(`/admin/email/${id}/reinvia`, { method: 'POST' })).status,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin', 'email'] }),
  });
}

// ---- utenti: dettaglio
export function useUtenteDettaglio(id: string) {
  return useQuery({ queryKey: chiaviAmm.utente(id), queryFn: async () => (await richiesta<UtenteDettaglio>(`/admin/utenti/${id}`)).dati });
}
export function useModificaUtente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { nome: string; cognome: string; email: string; ordine?: string; numeroAlbo?: string; motivo: string }) =>
      (await richiesta<UtenteDettaglio>(`/admin/utenti/${id}`, { method: 'PUT', json: input })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chiaviAmm.utente(id) });
      void qc.invalidateQueries({ queryKey: ['admin', 'utenti'] });
    },
  });
}
export function useSospensioneUtente(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { azione: 'sospendi' | 'riattiva'; motivo: string }) =>
      (await richiesta<UtenteDettaglio>(`/admin/utenti/${id}/${input.azione}`, { method: 'POST', json: { motivo: input.motivo } })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chiaviAmm.utente(id) });
      void qc.invalidateQueries({ queryKey: ['admin', 'utenti'] });
    },
  });
}
export function urlEsportaIscritti(percorsoId: string): string {
  return `${API_BASE}/admin/percorsi/${percorsoId}/iscritti/esporta`;
}

// ---- impostazioni
export function useImpostazioni() {
  return useQuery({ queryKey: chiaviAmm.impostazioni, queryFn: async () => (await richiesta<Impostazioni>('/admin/impostazioni')).dati });
}
export function useSalvaImpostazioni() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Omit<Impostazioni, 'prossimaCancellazione'>>) => (await richiesta<Impostazioni>('/admin/impostazioni', { method: 'PUT', json: input })).dati,
    onSuccess: (dati) => qc.setQueryData(chiaviAmm.impostazioni, dati),
  });
}

// ---- registro eventi
export interface FiltriRegistro {
  q?: string;
  azione?: string;
  actorId?: string;
  soggettoId?: string;
  da?: string;
  a?: string;
  pagina?: number;
  perPagina?: number;
}
export function useRegistro(filtri: FiltriRegistro) {
  return useQuery({
    queryKey: chiaviAmm.registro(filtri),
    queryFn: async () => (await richiesta<Paginato<EventoRegistro>>(`/admin/registro${qs(filtri)}`)).dati,
    placeholderData: (prev) => prev,
  });
}
export function useVerificaCatena() {
  return useMutation({
    mutationFn: async () => (await richiesta<{ integra: boolean; eventi: number; primoErroreId: string | null; verificataAt: string }>('/admin/registro/verifica')).dati,
  });
}
export function urlEsportaRegistro(da?: string, a?: string): string {
  return `${API_BASE}/admin/registro/esporta${qs({ da, a })}`;
}

// ---- GDPR
export function urlEsportazioneDati(): string {
  return `${API_BASE}/me/esportazione`;
}
export function useMiaCancellazione() {
  return useQuery({ queryKey: chiaviAmm.miaCancellazione, queryFn: async () => (await richiesta<{ stato: 'in_attesa' | 'approvata' | 'rifiutata' | null; richiestaAt: string | null }>('/me/cancellazione')).dati });
}
export function useChiediCancellazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { motivo?: string; password: string }) => (await richiesta<unknown>('/me/cancellazione', { method: 'POST', json: input })).status,
    onSuccess: () => void qc.invalidateQueries({ queryKey: chiaviAmm.miaCancellazione }),
  });
}
export function useCancellazioniAdmin(stato?: string) {
  return useQuery({ queryKey: chiaviAmm.cancellazioni(stato), queryFn: async () => (await richiesta<RichiestaCancellazione[]>(`/admin/cancellazioni${qs({ stato })}`)).dati });
}
export function useDecidiCancellazione() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; azione: 'approva' | 'rifiuta'; nota: string }) =>
      (await richiesta<RichiestaCancellazione>(`/admin/cancellazioni/${input.id}/${input.azione}`, { method: 'POST', json: { nota: input.nota } })).dati,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'cancellazioni'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'utenti'] });
    },
  });
}

// ---- TOTP
export function useTotpAvvia() {
  return useMutation({ mutationFn: async () => (await richiesta<{ segreto: string; otpauthUrl: string; qrDataUrl: string }>('/me/totp/avvia', { method: 'POST' })).dati });
}
export function useTotpConferma() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (codice: string) => (await richiesta<unknown>('/me/totp/conferma', { method: 'POST', json: { codice } })).status,
    onSuccess: () => void qc.invalidateQueries({ queryKey: chiaveMe }),
  });
}
export function useTotpDisattiva() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { password: string; codice: string }) => (await richiesta<unknown>('/me/totp/disattiva', { method: 'POST', json: input })).status,
    onSuccess: () => void qc.invalidateQueries({ queryKey: chiaveMe }),
  });
}
